import { useEffect, useRef } from "react";
import PropTypes from "prop-types";

export default function PaymentModal({
  id,
  isOpened,
  onClose,
  title,
  subtitle,
  children,
  primaryLabel,
  onPrimary,
  isPrimaryLoading,
  isPrimaryDisabled,
  cancelLabel,
  onCancel,
  maxWidth,
  hideLogo,
  hideFooter,
  className,
}) {
  const modalRef = useRef(null);

  // Show/hide modal programmatically using Bootstrap JS API
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;

    const bsModal =
      window.bootstrap?.Modal?.getOrCreateInstance(el, { backdrop: "static", keyboard: false });

    if (isOpened) {
      bsModal?.show();
    } else {
      bsModal?.hide();
    }
  }, [isOpened]);

  // Cleanup backdrop + body scroll-lock on unmount
  useEffect(() => {
    return () => {
      const el = modalRef.current;
      if (el) {
        window.bootstrap?.Modal?.getInstance(el)?.dispose();
      }
      const backdrop = document.querySelector(".modal-backdrop");
      if (backdrop) backdrop.remove();
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("padding-right");
      document.body.style.removeProperty("overflow");
    };
  }, []);

  const handleCancel = () => {
    if (onCancel) onCancel();
    else if (onClose) onClose();
  };

  return (
    <div
      ref={modalRef}
      className={`modal main-modal payment-modal fade ${className || ""}`}
      id={id}
      tabIndex="-1"
      aria-labelledby={`${id}-label`}
      aria-hidden="true"
    >
      <div
        className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
        style={{
          maxWidth: maxWidth ? `${maxWidth}px` : "520px",
          width: "100%",
        }}
      >
        <div className="modal-content payment-modal__content">
          <div className="modal-header payment-modal__header">
            <div className="payment-modal__header-left">
              {!hideLogo && (
                <img
                  src={'/images/business-logo-dark.svg'}
                  alt="Tadarab"
                  className="payment-modal__logo"
                />
              )}
            </div>

            <button
              type="button"
              className="payment-modal__close"
              aria-label="Close"
              onClick={onClose}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/*  Title block  */}
          {(title || subtitle) && (
            <div className="payment-modal__title-block">
              {title && (
                <h5 id={`${id}-label`} className="payment-modal__title">
                  {title}
                </h5>
              )}
              {subtitle && (
                <p className="payment-modal__subtitle">{subtitle}</p>
              )}
            </div>
          )}

          {/*  Body  */}
          <div className="modal-body payment-modal__body">{children}</div>

          {/*  Footer  */}
          {!hideFooter && (primaryLabel || cancelLabel) && (
            <div className="modal-footer payment-modal__footer">
              <button
                type="button"
                className="payment-modal__btn payment-modal__btn--cancel"
                onClick={handleCancel}
                disabled={isPrimaryLoading}
              >
                {cancelLabel || "Cancel"}
              </button>

              {primaryLabel && (
                <button
                  type="button"
                  className="payment-modal__btn payment-modal__btn--primary"
                  onClick={onPrimary}
                  disabled={isPrimaryDisabled || isPrimaryLoading}
                >
                  {isPrimaryLoading ? (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : null}
                  {primaryLabel}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

PaymentModal.propTypes = {
  id: PropTypes.string.isRequired,
  isOpened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node,
  primaryLabel: PropTypes.string,
  onPrimary: PropTypes.func,
  isPrimaryLoading: PropTypes.bool,
  isPrimaryDisabled: PropTypes.bool,
  cancelLabel: PropTypes.string,
  onCancel: PropTypes.func,
  maxWidth: PropTypes.number,
  hideLogo: PropTypes.bool,
  hideFooter: PropTypes.bool,
  className: PropTypes.string,
};

PaymentModal.defaultProps = {
  isPrimaryLoading: false,
  isPrimaryDisabled: false,
  hideLogo: false,
  hideFooter: false,
};
